import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames, roundNumber } from '../../../utils';
import { RegionAllocation } from '../hooks/useCellarStats';
import { ConcentrationWarning } from '../hooks/useCellarInsights';

interface RegionalBreakdownBarProps {
  allocations: RegionAllocation[];
  onRegionClick?: (regionId: string | null) => void;
  selectedRegion?: string | null;
  concentrationWarnings?: ConcentrationWarning[];
  onDismissConcentrationWarning?: (subject: string) => void;
  dismissedWarnings?: string[];
}

const RegionalBreakdownBar: FC<RegionalBreakdownBarProps> = ({
  allocations,
  onRegionClick,
  selectedRegion,
  concentrationWarnings = [],
  onDismissConcentrationWarning,
  dismissedWarnings = [],
}) => {
  const { t } = useTranslation();
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [hoveredWarning, setHoveredWarning] = useState<string | null>(null);

  const activeRegionWarnings = concentrationWarnings.filter(
    (w) => w.type === 'region' && !dismissedWarnings.includes(`region:${w.subject}`),
  );
  const regionWarningMap = new Map(activeRegionWarnings.map((w) => [w.subject.toLowerCase(), w]));

  if (!allocations || allocations.length === 0) {
    return null;
  }

  const handleClick = (regionId: string) => {
    if (onRegionClick) {
      onRegionClick(selectedRegion === regionId ? null : regionId);
    }
  };

  return (
    <div className="px-4 sm:px-6 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t('common:regionalAllocation')}</h3>
        {selectedRegion && (
          <button
            onClick={() => onRegionClick?.(null)}
            className="text-xs font-medium text-orange hover:text-orange/80 transition-colors"
          >
            {t('common:clearFilter')}
          </button>
        )}
      </div>

      {/* Stacked bar */}
      <div className="relative flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-slate-700">
        {allocations.map((region) => {
          const isHovered = hoveredRegion === region.id;
          const isSelected = selectedRegion === region.id;
          const isFiltered = selectedRegion && selectedRegion !== region.id;

          return (
            <div
              key={region.id}
              className={classNames(
                'relative cursor-pointer transition-all duration-200',
                isHovered ? 'scale-y-125 z-10' : '',
                isFiltered ? 'opacity-30' : '',
                isSelected ? 'ring-2 ring-gray-800 ring-offset-1 z-20' : '',
              )}
              style={{
                width: `${region.percentage}%`,
                backgroundColor: region.color,
                minWidth: region.percentage > 0 ? '8px' : 0,
              }}
              onClick={() => handleClick(region.id)}
              onMouseEnter={() => setHoveredRegion(region.id)}
              onMouseLeave={() => setHoveredRegion(null)}
            >
              {/* Tooltip */}
              {isHovered && (
                <div className="absolute z-30 bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 bg-gray-900 dark:bg-slate-600 text-white rounded-lg shadow-lg whitespace-nowrap text-xs">
                  <div className="font-semibold">{region.name}</div>
                  <div className="text-gray-300 mt-0.5">
                    {region.count} cases · {roundNumber(region.percentage, 1)}%
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                    <div className="border-4 border-transparent border-t-gray-900 dark:border-t-slate-600" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend - scrollable on mobile */}
      <div className="flex flex-wrap gap-2 mt-3">
        {allocations.map((region) => {
          const isSelected = selectedRegion === region.id;
          const isFiltered = selectedRegion && selectedRegion !== region.id;
          const warning = regionWarningMap.get(region.name.toLowerCase());
          const hasWarning = !!warning;

          return (
            <div key={region.id} className="relative">
              <button
                onClick={() => handleClick(region.id)}
                onMouseEnter={() => hasWarning && setHoveredWarning(region.id)}
                onMouseLeave={() => setHoveredWarning(null)}
                className={classNames(
                  'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200',
                  isSelected
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                    : isFiltered
                    ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500'
                    : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:shadow-sm',
                )}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: isSelected ? 'white' : region.color }}
                />
                <span className="truncate max-w-[80px] sm:max-w-none">{region.name}</span>
                <span
                  className={isSelected ? 'text-white/70 dark:text-gray-900/70' : 'text-gray-400 dark:text-gray-500'}
                >
                  {roundNumber(region.percentage, 0)}%
                </span>
                {hasWarning && (
                  <span
                    className={classNames(
                      'w-1.5 h-1.5 rounded-full flex-shrink-0',
                      warning.severity === 'elevated' ? 'bg-amber-500' : 'bg-amber-300',
                    )}
                  />
                )}
              </button>

              {/* Warning tooltip */}
              {hoveredWarning === region.id && warning && (
                <div className="absolute z-40 bottom-full left-0 mb-2 px-3 py-2 bg-white dark:bg-slate-700 rounded-xl shadow-xl border border-gray-200 dark:border-slate-600 text-xs max-w-[260px] whitespace-normal">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-amber-500">⚠️</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {t('common:insights.concentration.title')}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {t('common:insights.concentration.message', {
                      percentage: roundNumber(warning.percentage, 0),
                      region: warning.subject,
                    })}
                  </p>
                  {onDismissConcentrationWarning && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismissConcentrationWarning(`region:${warning.subject}`);
                        setHoveredWarning(null);
                      }}
                      className="mt-2 text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
                    >
                      {t('common:insights.dontShowAgain')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RegionalBreakdownBar;
